import { toast422, toastError } from '@components/Toast/Toast';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { globalErrors } from '../app/App';

const FAIL_SILENTLY_URLS = ['/api/v1/utilities/bank-info'];

interface Error422 {
	status: boolean;
	status_code: number;
	error_message: string;
	errors: {
		[key: string]: string[];
	};
}

interface Error400 {
	status: boolean;
	status_code: number;
	error_message: string;
	errors?: { message: string };
}
const isFailSilently = (err: AxiosError) => {
	const url = new URL(err.config?.url || '').pathname;

	// Get the last segment index
	const lastSlashIndex = url.lastIndexOf('/');

	// If there's a path parameter, remove it
	const baseUrl = lastSlashIndex > 0 ? url.substring(0, lastSlashIndex) : url;
	return FAIL_SILENTLY_URLS.includes(baseUrl);
};

const handle422 = (err: AxiosError) => {
	const errors = (err.response?.data as Error422)?.errors;

	const mappedErrors = Object.keys(errors).map((key: string) => ({
		property: key,
		message: errors[key][0],
	}));

	globalErrors.push(...mappedErrors);

	toast422(mappedErrors);
};

const handle400 = (err: AxiosError) => {
	const error = (err.response?.data as Error400)?.errors;
	toastError(error.message);
};

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			retry: 1,
		},
	},
	queryCache: new QueryCache({
		onError: (error) => {
			const axiosError = error as AxiosError;
			if (axiosError?.response?.status === 401) {
				localStorage.removeItem('token');
				window.location.reload();
			}
			if (axiosError?.response?.status === 422) {
				handle422(axiosError);
			}
			if (axiosError?.response?.status === 400 && !isFailSilently(axiosError)) {
				handle400(axiosError);
			}
			return;
		},
	}),
	mutationCache: new MutationCache({
		onError: (error, _variables, _context, mutation) => {
			if (mutation?.options?.meta?.suppressGlobalErrors) {
				return;
			}

			const axiosError = error as AxiosError;
			if (axiosError?.response?.status === 401) {
				localStorage.removeItem('token');
				window.location.reload();
			}
			if (axiosError?.response?.status === 422) {
				handle422(axiosError);
			}
			if (axiosError?.response?.status === 400 && !isFailSilently(axiosError)) {
				handle400(axiosError);
			}
		},
	}),
});
